import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Lawyer } from '../enterprise.types';
import { withUser } from '../../user/UserContext';
import { fetchEntrepriseLawyers } from '../enterprise.api';
import { doctrineAnalytics } from '../../utils/analyticsUtils';
import { EntityInfos } from '../../entity/expertises/infos/EntityInfos';
import { AllLawyersModal } from '../../modals/entity-lists/AllLawyersModal';
import { EntityDecisionListModal } from '../../modals/entity-lists/EntityDecisionListModal';
import { LawyerListWithDecisions } from '../../entity/entity-lists/LawyerListWithDecisions';
import { withModal, WithModalProps } from '../../components/modal/withModal';
import {
  withRestrictedModal,
  WithRestrictedModalProps,
} from '../../components/modal/components/modal-restricted/withRestrictedModal';
import { UserContextType } from '../../user/user.types';
import { InjectedIntlProps } from '../../doctrine.types';
import { withEnterprise } from './EnterpriseContext';
import { EnterpriseContextType } from './enterprise.types';
import {
  EntityBlock,
  EntityBlockTitle,
} from '../../entity/components/EntityBlockComponents';
import { SeeMore } from '../../components/see-more/SeeMore';

type Props = {
  displayTitle: Function;
} & UserContextType &
  WithRestrictedModalProps &
  WithModalProps &
  InjectedIntlProps &
  EnterpriseContextType;

type State = {
  lawyers: Array<Lawyer>;
  hasMore: boolean;
};

export class RawLawyersRepresentingEnterprise extends React.Component<
  Props,
  State
> {
  state: State = {
    lawyers: [],
    hasMore: false,
  };

  source = 'enterprise_lawyers';

  wording = this.props.intl.formatMessage(
    { id: 'app.pages.enterprise.representedBy.wording' },
    { companyName: this.props.enterpriseInfos.raisonSociale },
  );

  async componentDidMount() {
    const {
      enterpriseInfos: { siren },
      displayTitle,
    } = this.props;
    const { lawyers, hasMore } = (await fetchEntrepriseLawyers(siren)).data;
    this.setState({ lawyers, hasMore });
    if (lawyers.length > 0) {
      displayTitle();
    }
  }

  handleSeeMoreClick = () => {
    const {
      enterpriseInfos: { siren, raisonSociale },
      accessPremium,
      openModal,
      openRestrictedModal,
    } = this.props;
    if (accessPremium) {
      openModal(modalProps => (
        <AllLawyersModal
          {...modalProps}
          siren={siren}
          source={this.source}
          raisonSociale={raisonSociale}
          wording={this.wording}
        />
      ));
    } else {
      openRestrictedModal(`${this.source}_see_more`);
    }

    doctrineAnalytics('Enterprise SeeMoreLawyers', {
      siren,
    });
  };

  handleClickDecision = (lawyer: Lawyer) => {
    const {
      accessPremium,
      openRestrictedModal,
      openModal,
      enterpriseInfos: { siren },
    } = this.props;
    if (accessPremium) {
      openModal(modalProps => (
        <EntityDecisionListModal
          entityName={lawyer.nom}
          wording={this.wording}
          params={{
            lawyerId: lawyer.lawyerId,
            siren,
          }}
          source={this.source}
          {...modalProps}
        />
      ));
    } else {
      openRestrictedModal(`${this.source}_decision`);
    }
    doctrineAnalytics('Entity SeeEntityDecisions', {
      source: this.source,
    });
  };

  render() {
    const {
      enterpriseInfos: { raisonSociale },
      experience: { decisionCount },
    } = this.props;
    const { lawyers, hasMore } = this.state;
    if (!lawyers.length) return null;
    return (
      <EntityBlock>
        <EntityBlockTitle>
          <FormattedMessage
            id="app.pages.enterprise.representedBy.lawyer.title"
            values={{ numberOfLawyers: lawyers.length }}
          />
        </EntityBlockTitle>
        <EntityInfos
          decisionCount={decisionCount}
          entityType="enterprise"
          entityName={raisonSociale}
        />
        <LawyerListWithDecisions
          lawyers={lawyers}
          source={this.source}
          wording={this.wording}
          handleClickDecision={(lawyer: Lawyer) =>
            this.handleClickDecision(lawyer)
          }
        />
        {hasMore && (
          <SeeMore onClick={this.handleSeeMoreClick}>
            <FormattedMessage id="app.pages.enterprise.seeMore" />
          </SeeMore>
        )}
      </EntityBlock>
    );
  }
}

export const LawyersRepresentingEnterprise = withEnterprise(
  withModal(
    withRestrictedModal(withUser(injectIntl(RawLawyersRepresentingEnterprise))),
  ),
);
